import type { MouseEvent } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useMediaQuery, useTheme } from '@mui/material';
import { useDispatch } from 'react-redux';

import type { AdModel, AdsFilteringType, UserModel } from '@/types';
import { HeaderDialogsEnum, NotificationTypeEnum, UserTariffPlanEnum } from '@/types';
import { useDialogs } from '@/components/Dashboard/DialogsContext';
import { favoritesProvider } from '@/providers/favoritesProvider';
import { appSetNotification } from '@/store';
import { shouldRestrictByTariffPlan } from '@/utils/tariffPolicy';

import { createAdsListItemViewModel, rentFrequencyLabels } from './adsListItemHelpers';

interface UseAdsListItemControllerParams {
  filteringObj?: AdsFilteringType;
  item: AdModel;
  onSelectionClick?: (id: string, titleText: string) => void;
  tariffPlan?: UserTariffPlanEnum;
  type?: 'main' | 'favorite' | 'user';
  user?: UserModel;
  userId?: string;
}

interface GalleryState {
  imageIndex: number;
  itemId: string;
}

export const useAdsListItemController = ({
  filteringObj,
  item,
  onSelectionClick,
  tariffPlan,
  type,
  user,
  userId,
}: UseAdsListItemControllerParams) => {
  const dispatch = useDispatch();
  const { setOpenDialog } = useDialogs();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [galleryState, setGalleryState] = useState<GalleryState>({
    imageIndex: 0,
    itemId: item.id,
  });
  const [favorite, setFavorite] = useState(item.isFavorite);
  const favoriteRequestPendingRef = useRef(false);
  const latestFavoritePropRef = useRef({ id: item.id, isFavorite: item.isFavorite });
  const viewModel = createAdsListItemViewModel(item, filteringObj);
  const imageCount = viewModel.images.length;
  const imageIndex =
    galleryState.itemId === item.id &&
    galleryState.imageIndex >= 0 &&
    galleryState.imageIndex < imageCount
      ? galleryState.imageIndex
      : 0;

  useEffect(() => {
    setGalleryState((currentState) => {
      const normalizedImageIndex =
        currentState.itemId !== item.id ||
        currentState.imageIndex < 0 ||
        currentState.imageIndex >= imageCount
          ? 0
          : currentState.imageIndex;

      if (currentState.itemId === item.id && currentState.imageIndex === normalizedImageIndex) {
        return currentState;
      }

      return { imageIndex: normalizedImageIndex, itemId: item.id };
    });
  }, [imageCount, item.id]);

  useEffect(() => {
    latestFavoritePropRef.current = { id: item.id, isFavorite: item.isFavorite };

    if (!favoriteRequestPendingRef.current) {
      setFavorite(item.isFavorite);
    }
  }, [item.id, item.isFavorite]);

  const handleImageIndexChange = (event: MouseEvent<HTMLElement>, newIndex: number) => {
    event.preventDefault();
    const normalizedImageIndex =
      imageCount === 0 ? 0 : newIndex < 0 ? imageCount - 1 : newIndex >= imageCount ? 0 : newIndex;

    setGalleryState({ imageIndex: normalizedImageIndex, itemId: item.id });
  };

  const handleFavoriteClick = async (event: MouseEvent<SVGSVGElement>) => {
    event.preventDefault();
    event.stopPropagation();

    if (favoriteRequestPendingRef.current) return;

    favoriteRequestPendingRef.current = true;
    const requestItemId = item.id;
    const nextFavorite = !favorite;
    let requestSucceeded = false;

    try {
      const response = favorite
        ? await favoritesProvider.removeFromFavorites(item.id)
        : await favoritesProvider.addToFavorites(item.id);

      if (!response.ok) {
        dispatch(
          appSetNotification(
            NotificationTypeEnum.Error,
            response.message || 'Failed to update favorite',
            response.status
          )
        );
        return;
      }

      requestSucceeded = true;

      if (latestFavoritePropRef.current.id === requestItemId) {
        setFavorite(nextFavorite);
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to update favorite';
      dispatch(appSetNotification(NotificationTypeEnum.Error, message));
    } finally {
      favoriteRequestPendingRef.current = false;

      if (!requestSucceeded || latestFavoritePropRef.current.id !== requestItemId) {
        setFavorite(latestFavoritePropRef.current.isFavorite);
      }
    }
  };

  const handleSelectionClick = async (event: MouseEvent<SVGSVGElement>) => {
    event.preventDefault();
    event.stopPropagation();
    onSelectionClick?.(item.id, viewModel.titleText);
  };

  const handleLockedCreatorClick = () => {
    setOpenDialog((previousState) => ({
      ...previousState,
      [HeaderDialogsEnum.Tariff]: !previousState[HeaderDialogsEnum.Tariff],
    }));
  };

  const isTariffFree = shouldRestrictByTariffPlan(tariffPlan);
  const userName = isTariffFree
    ? 'Creator of property'
    : user
      ? user.name
      : item.propertyAdCreator?.name;
  const agencyName = isTariffFree ? 'Creator Employer of Ad' : item.propertyAdCreatorEmployer?.name;
  const creatorHref =
    userId == item.propertyAdCreator?.id ? '/profile/view' : `/users/${item.propertyAdCreator?.id}`;
  const creatorEmployerHref =
    userId == item.propertyAdCreatorEmployer?.id
      ? '/profile/view'
      : `/users/${item.propertyAdCreatorEmployer?.id}`;
  const currentImageUrl = viewModel.images[imageIndex]?.fileUrl;
  const imgUrl = currentImageUrl ? `${currentImageUrl}_${isMobile ? '370' : '482'}x238` : '';

  return {
    creatorInfoProps: {
      agencyName,
      creator: item.propertyAdCreator,
      creatorEmployer: item.propertyAdCreatorEmployer,
      creatorEmployerHref,
      creatorHref,
      isTariffFree,
      onLockedCreatorClick: handleLockedCreatorClick,
      user,
      userName,
    },
    mainInfoProps: {
      address: viewModel.address,
      area: viewModel.area,
      baths: item.baths,
      beds: item.beds,
      convertedAreaUnits: item.convertedAreaUnits,
      convertedCurrency: item.convertedCurrency,
      defaultRentFrequency: item.defaultRentFrequency,
      filterRentFrequency: viewModel.filterRentFrequency,
      id: item.id,
      isNotCommercial: viewModel.isNotCommercial,
      isSale: viewModel.isSale,
      multilistingText: viewModel.multilistingText,
      price: viewModel.price,
      rentFrequencyLabels,
      textBaths: viewModel.textBaths,
      textBeds: viewModel.textBeds,
      title: item.title,
      titleText: viewModel.titleText,
    },
    mediaProps: {
      addCommission: item.addCommission,
      currencyCommission: item.currencyCommission,
      favorite,
      hasVideo: viewModel.hasVideo,
      id: item.id,
      imageIndex,
      images: viewModel.images,
      imgUrl,
      isCreatorRestricted: isTariffFree,
      isSale: viewModel.isSale,
      onFavoriteClick: handleFavoriteClick,
      onImageIndexChange: handleImageIndexChange,
      onSelectionClick: handleSelectionClick,
      parsedUpdateDate: viewModel.parsedUpdateDate,
      showSelectionAction: type === 'main',
      showTopAgent: !user && item.propertyAdCreator?.isAmbassador === true,
      title: item.title,
      transactionDate: viewModel.transactionDate,
      transactionType: item.transactionType,
    },
  };
};
